import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePixels } from '@/contexts/PixelContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/utils/api';
import {
  Eye, EyeOff, Mail, Lock, User, Phone,
  AlertCircle, Loader2, CheckCircle2, ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react';

// Small brand-tinted SVG glyph. The Google-brand mark can't be recolored
// so we render the multi-color G at fixed size to match the button height.
const GoogleGlyph = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const RegisterPage = () => {
  const [params] = useSearchParams();
  // Bug Z4: post-RSVP claim flow. When these params are present we
  // prefill from the guest RSVP so the user isn't forced to re-type
  // their name / phone. The password field remains the only visible
  // required field by default; the rest sit behind an "Edit your
  // details" toggle.
  const claimMode = params.get('claim') === '1';
  const claimEditToken = params.get('edit_token') || '';
  const claimRegId = params.get('reg_id') || '';
  const claimEmail = params.get('email') || '';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: claimEmail,
    phone: '',
    dob: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showDetails, setShowDetails] = useState(!claimMode);
  const [prefilling, setPrefilling] = useState(claimMode && !!claimEditToken);
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);

  const { register, loading } = useAuth();
  const { trackEvent } = usePixels();
  const navigate = useNavigate();

  // Bug Z4: on mount, if this is a claim link, pull the guest details from
  // /rsvp/edit/{edit_token} and prefill the form.
  useEffect(() => {
    if (!claimMode || !claimEditToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/rsvp/edit/${encodeURIComponent(claimEditToken)}`);
        const a = data?.registration?.answers || {};
        const email = data?.registration?.email || claimEmail;
        const phone = data?.registration?.phone || '';
        const fullName = (a.full_name || '').trim();
        const prefFirst = (a.preferred_first_name || '').trim();
        let firstName = prefFirst || (fullName.split(' ')[0] || '');
        let lastName = '';
        if (fullName) {
          const parts = fullName.split(' ');
          if (parts.length > 1) lastName = parts.slice(1).join(' ');
        }
        setFormData((cur) => ({
          ...cur,
          email,
          phone,
          first_name: firstName,
          last_name: lastName,
        }));
      } catch (e) {
        // fall back to the query-param email; guest can edit details manually
      } finally {
        setPrefilling(false);
      }
    })();
  }, [claimMode, claimEditToken, claimEmail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setEmailAlreadyRegistered(false);

    if (name === 'password') {
      let strength = 0;
      if (value.length >= 8) strength++;
      if (/[a-z]/.test(value) && /[A-Z]/.test(value)) strength++;
      if (/[0-9]/.test(value)) strength++;
      if (/[^a-zA-Z0-9]/.test(value)) strength++;
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setEmailAlreadyRegistered(false);

    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      setLocalError('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      setLocalError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    const result = await register({
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone || null,
      dob: formData.dob || null,
      password: formData.password,
    });

    if (result.success) {
      // Meta Sign Up analytics: fire CompleteRegistration on account
      // creation (Batch 4). Every /register hit is a fresh user
      // (backend guarantees is_new_user=true), so we fire
      // unconditionally. content_category='signup' distinguishes this
      // from the RSVP-ticket CompleteRegistration in PaymentSuccessPage
      // ('rsvp_ticket') so marketing can filter in Meta Events Manager.
      try {
        trackEvent('CompleteRegistration', {
          content_name: 'account_signup',
          content_category: 'signup',
          method: 'email',
        });
      } catch (_) { /* never break the flow */ }

      // Bug Z4: link the guest RSVP to the new user (best effort). Token
      // is verified server-side; if it fails we still land the user on
      // /onboarding so they aren't stuck.
      if (claimMode && claimEditToken && claimRegId) {
        try {
          await api.post('/rsvp/claim', {
            edit_token: claimEditToken,
            registration_id: claimRegId,
          });
        } catch (_) { /* non-fatal */ }
      }
      navigate('/onboarding');
    } else {
      const msg = (result.error || '').toLowerCase();
      if (msg.includes('already registered')) {
        setEmailAlreadyRegistered(true);
      } else {
        setLocalError(result.error);
      }
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength === 0) return 'bg-neutral-200';
    if (passwordStrength === 1) return 'bg-red-500';
    if (passwordStrength === 2) return 'bg-orange-500';
    if (passwordStrength === 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength === 1) return 'Weak';
    if (passwordStrength === 2) return 'Fair';
    if (passwordStrength === 3) return 'Good';
    return 'Strong';
  };

  if (prefilling) {
    return (
      <div className="py-12 text-center text-neutral-500">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
        Loading your details...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="font-display text-3xl font-bold text-primary-900 mb-2">
          {claimMode ? 'Save your Dostination profile' : 'Create Account'}
        </h1>
        <p className="text-neutral-600">
          {claimMode
            ? 'One password to keep your RSVP, saved details, and future events.'
            : 'Create your account to RSVP to gatherings and travel'}
        </p>
      </div>

      {claimMode && !prefilling && (
        <div className="rounded-lg border border-secondary-200 bg-secondary-50/60 p-3 flex items-center gap-2 text-sm text-primary-900" data-testid="claim-prefill-banner">
          <Sparkles className="h-4 w-4 text-secondary-700 shrink-0" />
          <span>We prefilled your details from your RSVP. Just pick a password.</span>
        </div>
      )}

      {claimMode && (
        <div className="space-y-3" data-testid="google-signin-block">
          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.href;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 rounded-xl border border-secondary-300 bg-gradient-to-r from-white to-secondary-50 px-4 py-3 text-primary-900 font-semibold text-sm shadow-sm hover:shadow-gold hover:from-secondary-50 hover:to-secondary-100 transition-all"
            data-testid="continue-with-google-btn"
          >
            <GoogleGlyph className="h-5 w-5" />
            <span>Continue with Google</span>
          </button>
          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-neutral-200" />
            <span className="mx-3 text-[11px] uppercase tracking-widest text-neutral-500">or</span>
            <div className="flex-grow border-t border-neutral-200" />
          </div>
        </div>
      )}

      {!claimMode && (
        <div className="space-y-3" data-testid="google-signin-block-fresh">
          <button
            type="button"
            onClick={() => {
              const redirectUrl = window.location.href;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 rounded-xl border border-secondary-300 bg-gradient-to-r from-white to-secondary-50 px-4 py-3 text-primary-900 font-semibold text-sm shadow-sm hover:shadow-gold hover:from-secondary-50 hover:to-secondary-100 transition-all"
            data-testid="register-google-btn"
          >
            <GoogleGlyph className="h-5 w-5" />
            <span>Continue with Google</span>
          </button>
          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-neutral-200" />
            <span className="mx-3 text-[11px] uppercase tracking-widest text-neutral-500">or sign up with email</span>
            <div className="flex-grow border-t border-neutral-200" />
          </div>
        </div>
      )}

      {emailAlreadyRegistered && (
        <Alert data-testid="email-already-registered-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="text-sm">
              An account with <strong>{formData.email}</strong> already exists.
            </div>
            <Link
              to={`/auth/login?email=${encodeURIComponent(formData.email)}`}
              className="inline-block mt-2 text-secondary-600 font-semibold hover:underline"
              data-testid="sign-in-instead-link"
            >
              Sign in instead →
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {localError && !emailAlreadyRegistered && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{localError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {claimMode && !showDetails && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-3 text-sm text-neutral-700">
            <div><strong>{formData.first_name} {formData.last_name}</strong></div>
            <div className="text-xs text-neutral-500">{formData.email}{formData.phone ? ` · ${formData.phone}` : ''}</div>
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-secondary-700 hover:text-secondary-800 font-semibold"
              data-testid="edit-your-details-btn"
            >
              <ChevronDown className="h-3 w-3" /> Edit your details
            </button>
          </div>
        )}

        {(!claimMode || showDetails) && (
          <>
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Date of birth (Bundle 138 - Stripe compliance 18+ gate) */}
            <div className="space-y-2">
              <Label htmlFor="dob">Date of birth *</Label>
              <Input
                id="dob"
                name="dob"
                type="date"
                value={formData.dob}
                onChange={handleChange}
                disabled={loading}
                data-testid="register-dob-input"
              />
              <p className="text-[11px] text-neutral-500" data-testid="register-age-gate-copy">
                Dostination is for adults 18 and older. Users must confirm they are at least 18 to
                create an account. Accounts found in violation will be permanently removed.
              </p>
            </div>

            {claimMode && (
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700"
                data-testid="hide-your-details-btn"
              >
                <ChevronUp className="h-3 w-3" /> Hide details
              </button>
            )}
          </>
        )}

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              value={formData.password}
              onChange={handleChange}
              className="pl-10 pr-10"
              disabled={loading}
              data-testid="register-password-input"
              autoFocus={claimMode}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {formData.password && (
            <div className="space-y-1">
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      level <= passwordStrength ? getPasswordStrengthColor() : 'bg-neutral-200'
                    }`}
                  />
                ))}
              </div>
              {passwordStrength > 0 && (
                <p className="text-xs text-neutral-600">
                  Password strength: {getPasswordStrengthText()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="pl-10"
              disabled={loading}
              data-testid="register-confirm-password-input"
            />
            {formData.confirmPassword && formData.password === formData.confirmPassword && (
              <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
            )}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={loading}
          data-testid="register-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Creating account...
            </>
          ) : (
            claimMode ? 'Save my account' : 'Create Account'
          )}
        </Button>
      </form>

      <div className="text-center text-sm text-neutral-600">
        Already have an account?{' '}
        <Link
          to={`/auth/login${formData.email ? `?email=${encodeURIComponent(formData.email)}` : ''}`}
          className="text-secondary-500 font-semibold hover:text-secondary-600 hover:underline"
        >
          Sign in
        </Link>
      </div>

      <p className="text-xs text-neutral-500 text-center">
        By creating an account, you agree to our{' '}
        <Link to="/terms" className="underline">Terms of Service</Link>
        {' '}and{' '}
        <Link to="/privacy" className="underline">Privacy Policy</Link>
      </p>
    </div>
  );
};

export default RegisterPage;
